import type { PlaybookSetsQuery } from "@qw/graphql";
import { Badge, Button, Icon, Tooltip, TooltipContent, TooltipTrigger } from "@qw/ui";

type PlaybookSet = NonNullable<PlaybookSetsQuery["playbookSets"]>[number];
type Playbook = NonNullable<PlaybookSet["playbooks"]>[number];

interface PlaybookSetCardProps {
  id: string;
  name: string;
  playbookCount: number;
  isSubscribed: boolean;
  playbooks?: Playbook[] | null;
  onSubscribe: (setId: string) => void;
  onUnsubscribe: (setId: string) => void;
  onViewSet: (setId: string) => void;
}

export const PlaybookSetCard = ({
  id,
  name,
  playbookCount,
  isSubscribed,
  playbooks,
  onSubscribe,
  onUnsubscribe,
  onViewSet,
}: PlaybookSetCardProps) => {
  return (
    <div
      className="p-4 bg-card border rounded flex flex-col justify-between cursor-pointer hover:border-primary/50 transition-colors"
      onClick={() => onViewSet(id)}
    >
      <Icon icon="folder" className="mb-4" />
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg text-accent-foreground font-medium">{name}</h3>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" size="xs" className="text-2xs gap-1 text-muted-foreground">
                <Icon icon="rate_review" className="text-xs" />
                {playbookCount}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <ul className="flex flex-col gap-1">
                {playbooks?.map((p) => (
                  <li key={p.id}>{p.name}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        </div>

        {isSubscribed ? (
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onUnsubscribe(id);
            }}
            className="bg-background"
          >
            <Icon icon="group_remove" className="mr-1 text-xs" />
            Unsubscribe
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSubscribe(id);
                }}
              >
                <Icon icon="add_box" className="mr-1 text-xs" />
                Subscribe
              </Button>
            </TooltipTrigger>
            <TooltipContent>Subscribe to all playbooks in this set</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};
